import type {
  AnnouncementRepository,
  CreateAnnouncementInput,
  TenantContext,
  UpdateAnnouncementInput,
} from '@zetyar/data-contracts'
import type { Announcement, UUID } from '@zetyar/domain'
import type { SupabaseClient } from '@supabase/supabase-js'

const ANNOUNCEMENT_SELECT = `
  id, organization_id, title, content, summary, is_pinned, is_draft, scheduled_at,
  published_at, audience_type, created_at, updated_at, deleted_at,
  author_membership:memberships!announcements_author_membership_id_fkey (id, display_name),
  reads:announcement_reads (membership_id)
`

interface AnnouncementRow {
  readonly id: UUID
  readonly organization_id: UUID
  readonly title: string
  readonly content: string
  readonly summary: string | null
  readonly is_pinned: boolean
  readonly is_draft: boolean
  readonly scheduled_at: string | null
  readonly published_at: string | null
  readonly audience_type: string
  readonly created_at: string
  readonly updated_at: string
  readonly deleted_at: string | null
  readonly author_membership: { readonly id: UUID; readonly display_name: string } | null
  readonly reads: readonly { readonly membership_id: UUID }[] | null
}

function mapRow(row: AnnouncementRow, currentMembershipId?: UUID): Announcement {
  return {
    id: row.id,
    organizationId: row.organization_id,
    title: row.title,
    content: row.content,
    summary: row.summary,
    isPinned: row.is_pinned,
    isDraft: row.is_draft,
    scheduledAt: row.scheduled_at,
    publishedAt: row.published_at,
    audience: row.audience_type === 'all' ? { type: 'all' } : { type: 'roles' },
    attachments: [],
    createdBy: row.author_membership?.id ?? '',
    updatedBy: null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    deletedAt: row.deleted_at,
    author: {
      id: row.author_membership?.id ?? '',
      firebaseUid: '',
      email: '',
      displayName: row.author_membership?.display_name ?? '',
      avatarUrl: null,
      phone: null,
      emailVerified: true,
      isSuperAdmin: false,
      status: 'active',
      lastLoginAt: null,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    },
    isRead: (row.reads ?? []).some((r) => r.membership_id === currentMembershipId),
    readCount: row.reads?.length ?? 0,
  }
}

function mapRpcAnnouncement(row: Record<string, unknown>): Announcement {
  return mapRow({
    id: row.id as UUID,
    organization_id: row.organization_id as UUID,
    title: row.title as string,
    content: row.content as string,
    summary: (row.summary as string | null) ?? null,
    is_pinned: row.is_pinned as boolean,
    is_draft: row.is_draft as boolean,
    scheduled_at: (row.scheduled_at as string | null) ?? null,
    published_at: (row.published_at as string | null) ?? null,
    audience_type: row.audience_type as string,
    created_at: row.created_at as string,
    updated_at: row.updated_at as string,
    deleted_at: (row.deleted_at as string | null) ?? null,
    author_membership: null,
    reads: null,
  })
}

export function createSupabaseAnnouncementRepository(
  client: SupabaseClient,
): AnnouncementRepository {
  return {
    async listVisible(context: TenantContext, { limit }) {
      const { data, error } = await client
        .from('announcements')
        .select(ANNOUNCEMENT_SELECT)
        .eq('organization_id', context.organizationId)
        .eq('is_draft', false)
        .is('deleted_at', null)
        .order('is_pinned', { ascending: false })
        .order('published_at', { ascending: false })
        .limit(Math.max(1, Math.min(limit, 50)))

      if (error) throw error

      return {
        data: (data as unknown as AnnouncementRow[]).map((row) =>
          mapRow(row, context.membershipId),
        ),
        nextCursor: null,
        hasMore: false,
      }
    },

    async acknowledgeRead(context: TenantContext, announcementId: UUID) {
      const { error } = await client.rpc('acknowledge_announcement_read', {
        p_announcement_id: announcementId,
        p_organization_id: context.organizationId,
      })
      if (error) throw error
    },

    async create(context: TenantContext, input: CreateAnnouncementInput) {
      const { data, error } = await client
        .rpc('create_announcement', {
          p_organization_id: context.organizationId,
          p_title: input.title,
          p_content: input.content,
          p_summary: input.summary ?? null,
          p_is_pinned: input.isPinned ?? false,
          p_is_draft: input.isDraft ?? true,
          p_audience_type: input.audienceType ?? 'all',
        })
        .single()

      if (error) throw error
      return mapRpcAnnouncement(data as Record<string, unknown>)
    },

    async update(context: TenantContext, announcementId: UUID, input: UpdateAnnouncementInput) {
      const { data, error } = await client
        .rpc('update_announcement', {
          p_id: announcementId,
          p_organization_id: context.organizationId,
          p_title: input.title ?? null,
          p_content: input.content ?? null,
          p_summary: input.summary ?? null,
          p_is_pinned: input.isPinned ?? null,
          p_is_draft: input.isDraft ?? null,
        })
        .single()

      if (error) throw error
      return mapRpcAnnouncement(data as Record<string, unknown>)
    },

    async delete(context: TenantContext, announcementId: UUID) {
      if (!context.membershipId) throw new Error('Membership is required to delete an announcement')

      const { error } = await client.rpc('delete_announcement', {
        p_id: announcementId,
        p_organization_id: context.organizationId,
      })
      if (error) throw error
    },
  }
}
